import type { Alert } from './types'

/**
 * Recurring-event detection.
 *
 * A single congestion alert on a cell is weather. The same cell firing six times in an
 * hour is a road, and it belongs in a different part of the operator's attention: one is
 * something to watch, the other is something to escalate. The topology deliberately does
 * not do this -- CongestionEvalProcessor has hysteresis and a cooldown so it does not
 * spam, which is exactly the behaviour that hides a chronic site behind a tidy stream of
 * individual alerts.
 *
 * Done here rather than in a processor on purpose: this is a presentation concern with a
 * window the operator controls, and putting it in the topology would mean a rebuild every
 * time someone wanted a different threshold.
 */

export interface RecurringGroup {
  /** Stable identity: what recurred, and where. */
  key: string
  kind: string
  /** vehicle_id or cell — whichever the alert is about. */
  subject: string
  subjectKind: 'vehicle' | 'cell' | 'fleet'
  count: number
  firstTs: number
  lastTs: number
  /** Highest severity seen in the group. */
  severity: Alert['severity']
  /** The most recent alert, for the message. */
  latest: Alert
}

const SEVERITY_RANK: Record<string, number> = { INFO: 0, WARN: 1, CRITICAL: 2 }

export function subjectOf(a: Alert): { subject: string; kind: RecurringGroup['subjectKind'] } {
  if (a.vehicle_id) return { subject: a.vehicle_id, kind: 'vehicle' }
  if (a.cell) return { subject: a.cell, kind: 'cell' }
  return { subject: 'fleet', kind: 'fleet' }
}

/**
 * Group alerts by (kind, subject) inside a trailing window and keep the groups that
 * crossed the threshold. Sorted by count, because "how chronic" is the question being
 * asked -- recency is already answered by the tape.
 */
export function findRecurring(
  alerts: readonly Alert[],
  opts: { windowMs?: number; minCount?: number; now?: number } = {},
): RecurringGroup[] {
  const windowMs = opts.windowMs ?? 60 * 60 * 1000
  const minCount = opts.minCount ?? 3
  const now = opts.now ?? Date.now()
  const cutoff = now - windowMs

  const groups = new Map<string, RecurringGroup>()
  for (const a of alerts) {
    if (!a.ts || a.ts < cutoff) continue
    const { subject, kind } = subjectOf(a)
    const key = `${a.kind}|${subject}`
    const g = groups.get(key)
    if (!g) {
      groups.set(key, {
        key, kind: a.kind, subject, subjectKind: kind,
        count: 1, firstTs: a.ts, lastTs: a.ts,
        severity: a.severity, latest: a,
      })
      continue
    }
    g.count += 1
    if (a.ts < g.firstTs) g.firstTs = a.ts
    if (a.ts > g.lastTs) { g.lastTs = a.ts; g.latest = a }
    if ((SEVERITY_RANK[a.severity] ?? 0) > (SEVERITY_RANK[g.severity] ?? 0)) g.severity = a.severity
  }

  return [...groups.values()]
    .filter((g) => g.count >= minCount)
    .sort((a, b) => b.count - a.count || b.lastTs - a.lastTs)
}

/**
 * Same vehicle, same overspeed kind, same route, no gap over LEG_GAP_MS: collapsed into
 * one card (the latest event, carrying a count) instead of one card per firing.
 *
 * Alert has no leg/trip id, so there is no exact "still the same leg" test. A route-bounded
 * time gap is the closest available proxy: route_id flips between a leg's laden and empty
 * direction, so it already splits legs cleanly, and a multi-hour gap on the same route is
 * far more likely to be a new day's leg than a pause inside one. LEG_GAP_MS is deliberately
 * long -- a leg runs hours end to end, with real quiet stretches at tolls and halts, and
 * splitting those into extra cards is the exact bug being fixed here.
 */
const LEG_GAP_MS = 3 * 60 * 60 * 1000
const SAME_LEG_KINDS = new Set(['OVERSPEED', 'GHAT_OVERSPEED'])

export function collapseSameLegAlerts(alerts: readonly Alert[]): Alert[] {
  const rest: Alert[] = []
  const byKey = new Map<string, Alert[]>()
  for (const a of alerts) {
    if (!SAME_LEG_KINDS.has(a.kind) || !a.vehicle_id) {
      rest.push(a)
      continue
    }
    const key = `${a.kind}|${a.vehicle_id}|${a.route_id ?? ''}`
    const list = byKey.get(key)
    if (list) list.push(a)
    else byKey.set(key, [a])
  }

  const out = [...rest]
  for (const list of byKey.values()) {
    list.sort((x, y) => x.ts - y.ts)
    let run: Alert[] = []
    const flush = () => {
      const first = run[0]
      const latest = run[run.length - 1]
      if (!first || !latest) return
      out.push(run.length === 1
        ? latest
        : { ...latest, _groupCount: run.length, _groupFirstTs: first.ts })
      run = []
    }
    for (const a of list) {
      const last = run[run.length - 1]
      if (last && a.ts - last.ts > LEG_GAP_MS) flush()
      run.push(a)
    }
    flush()
  }
  return out
}

/** Distinct alert kinds present, for the filter chips. Sorted by frequency. */
export function kindsOf(alerts: readonly Alert[]): { kind: string; count: number }[] {
  const c = new Map<string, number>()
  for (const a of alerts) c.set(a.kind, (c.get(a.kind) ?? 0) + 1)
  return [...c.entries()]
    .map(([kind, count]) => ({ kind, count }))
    .sort((a, b) => b.count - a.count || a.kind.localeCompare(b.kind))
}

export interface AlertFilter {
  severities: Set<string>
  kinds: Set<string>
  subject: string
  sinceMs: number | null
}

export function emptyFilter(): AlertFilter {
  return { severities: new Set(), kinds: new Set(), subject: '', sinceMs: null }
}

/** An empty set means "no constraint", not "match nothing". */
export function applyFilter(
  alerts: readonly Alert[], f: AlertFilter, now = Date.now(),
): Alert[] {
  const q = f.subject.trim().toLowerCase()
  return alerts.filter((a) => {
    if (f.severities.size && !f.severities.has(a.severity)) return false
    if (f.kinds.size && !f.kinds.has(a.kind)) return false
    if (f.sinceMs != null && (!a.ts || a.ts < now - f.sinceMs)) return false
    if (q) {
      const hay = `${a.vehicle_id ?? ''} ${a.cell ?? ''} ${a.message ?? ''}`.toLowerCase()
      if (!hay.includes(q)) return false
    }
    return true
  })
}
